import React, { useEffect, useState } from "react";
import { Save, Plus, Trash2, Loader2, Users } from "lucide-react";
import { adminApi } from "../../lib/api";
import { Button } from "../ui/button";
import { useToast } from "../../hooks/use-toast";

const inputCls = "w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange/40";

const Field = ({ label, value, onChange, area, placeholder }) => (
  <label className="block">
    <span className="text-xs font-semibold text-navy">{label}</span>
    {area
      ? <textarea rows={2} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={`${inputCls} mt-1`} />
      : <input value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={`${inputCls} mt-1`} />}
  </label>
);

const Card = ({ title, children }) => (
  <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 mb-6">
    <h2 className="font-display font-bold text-navy mb-4">{title}</h2>
    {children}
  </div>
);

export default function TaxDatesManager() {
  const { toast } = useToast();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [reminders, setReminders] = useState([]);
  const [tab, setTab] = useState("content");

  useEffect(() => {
    adminApi.taxDates.getConfig().then(setForm).catch(() => {});
    adminApi.taxDates.reminders().then(setReminders).catch(() => {});
  }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const setCat = (ci, patch) => set("categories", form.categories.map((c, i) => (i === ci ? { ...c, ...patch } : c)));
  const setDate = (ci, di, patch) => setCat(ci, { dates: form.categories[ci].dates.map((d, i) => (i === di ? { ...d, ...patch } : d)) });
  const addDate = (ci) => setCat(ci, { dates: [...(form.categories[ci].dates || []), { date: "", title: "", detail: "", recurring: true }] });
  const removeDate = (ci, di) => setCat(ci, { dates: form.categories[ci].dates.filter((_, i) => i !== di) });

  const save = async () => {
    setSaving(true);
    try { const updated = await adminApi.taxDates.saveConfig(form); setForm(updated); toast({ title: "Tax dates saved" }); }
    catch { toast({ title: "Couldn't save", variant: "destructive" }); }
    finally { setSaving(false); }
  };

  const removeReminder = async (id) => {
    await adminApi.taxDates.removeReminder(id).catch(() => {});
    setReminders((l) => l.filter((x) => x.id !== id));
  };

  if (!form) return <p className="text-sm text-slate-400">Loading…</p>;

  return (
    <div data-testid="tax-dates-manager">
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h1 className="font-display font-extrabold text-2xl text-navy">Key Tax Dates</h1>
          <p className="text-sm text-slate-500">Edit the deadline list, reminder copy, and view reminder sign-ups.</p>
        </div>
        {tab === "content" && (
          <Button onClick={save} disabled={saving} className="bg-orange hover:bg-orange-dark text-white rounded-full shrink-0" data-testid="tax-dates-save">
            {saving ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <Save size={15} className="mr-1.5" />} Save changes
          </Button>
        )}
      </div>

      <div className="flex gap-2 mb-6">
        <button onClick={() => setTab("content")} data-testid="tax-tab-content" className={`px-4 py-2 rounded-full text-sm font-semibold ${tab === "content" ? "bg-navy text-white" : "bg-white border border-slate-200 text-navy"}`}>Dates & copy</button>
        <button onClick={() => setTab("reminders")} data-testid="tax-tab-reminders" className={`px-4 py-2 rounded-full text-sm font-semibold ${tab === "reminders" ? "bg-navy text-white" : "bg-white border border-slate-200 text-navy"}`}>Reminder sign-ups ({reminders.length})</button>
      </div>

      {tab === "content" ? (
        <>
          <Card title="Page copy">
            <div className="space-y-4">
              <Field label="Title" value={form.title} onChange={(v) => set("title", v)} />
              <Field label="Intro" value={form.intro} onChange={(v) => set("intro", v)} area />
              <label className="flex items-center gap-2 text-sm font-semibold text-navy cursor-pointer">
                <input type="checkbox" checked={!!form.reminders_enabled} onChange={(e) => set("reminders_enabled", e.target.checked)} className="w-4 h-4 accent-orange" data-testid="tax-reminders-toggle" />
                Show the email reminder opt-in
              </label>
              <div className="max-w-[220px]">
                <Field label="Send reminders how many days before?" value={form.reminder_days} onChange={(v) => set("reminder_days", Number(v) || 0)} placeholder="7" />
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <Field label="Reminder heading" value={form.reminder_heading} onChange={(v) => set("reminder_heading", v)} />
                <Field label="Reminder body" value={form.reminder_body} onChange={(v) => set("reminder_body", v)} area />
              </div>
            </div>
          </Card>

          {(form.categories || []).map((cat, ci) => (
            <Card key={ci} title="">
              <div className="flex items-center gap-3 mb-4">
                <input value={cat.name} onChange={(e) => setCat(ci, { name: e.target.value })} className={`${inputCls} font-display font-bold text-navy`} data-testid={`tax-cat-name-${ci}`} />
                <button onClick={() => set("categories", form.categories.filter((_, i) => i !== ci))} className="text-red-400 hover:text-red-600 shrink-0" data-testid={`tax-cat-remove-${ci}`}><Trash2 size={16} /></button>
              </div>
              <div className="space-y-3">
                {(cat.dates || []).map((d, di) => (
                  <div key={di} className="rounded-xl border border-slate-100 bg-slate-50 p-4 relative">
                    <button onClick={() => removeDate(ci, di)} className="absolute top-3 right-3 text-red-400 hover:text-red-600" data-testid={`tax-date-remove-${ci}-${di}`}><Trash2 size={15} /></button>
                    <div className="grid md:grid-cols-2 gap-3 pr-6">
                      <Field label="Date / timing" value={d.date} onChange={(v) => setDate(ci, di, { date: v })} placeholder="31 January" />
                      <Field label="Title" value={d.title} onChange={(v) => setDate(ci, di, { title: v })} />
                    </div>
                    <div className="mt-3 pr-6"><Field label="Detail" value={d.detail} onChange={(v) => setDate(ci, di, { detail: v })} area /></div>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={() => addDate(ci)} className="rounded-full" data-testid={`tax-date-add-${ci}`}><Plus size={14} className="mr-1" /> Add date</Button>
              </div>
            </Card>
          ))}
          <Button variant="outline" onClick={() => set("categories", [...(form.categories || []), { name: "New category", dates: [] }])} className="rounded-full" data-testid="tax-cat-add"><Plus size={14} className="mr-1" /> Add category</Button>
        </>
      ) : (
        <Card title={`Reminder sign-ups (${reminders.length})`}>
          {reminders.length === 0 ? (
            <p className="text-sm text-slate-400 flex items-center gap-2"><Users size={15} /> No sign-ups yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-slate-400 border-b border-slate-100">
                  <th className="py-2 pr-3">Name</th><th className="py-2 pr-3">Email</th><th className="py-2 pr-3">Consent</th><th className="py-2 pr-3">Date</th><th></th>
                </tr></thead>
                <tbody>
                  {reminders.map((l) => (
                    <tr key={l.id} className="border-b border-slate-50" data-testid={`tax-reminder-${l.id}`}>
                      <td className="py-2 pr-3 text-navy">{l.name || "-"}</td>
                      <td className="py-2 pr-3">{l.email}</td>
                      <td className="py-2 pr-3">{l.marketing_consent ? "Yes" : "No"}</td>
                      <td className="py-2 pr-3 text-slate-400">{(l.created_at || "").slice(0, 10)}</td>
                      <td className="py-2"><button onClick={() => removeReminder(l.id)} className="text-red-400 hover:text-red-600" data-testid={`tax-reminder-remove-${l.id}`}><Trash2 size={15} /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
